import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import AdminNavbar from './AdminNavbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGauge, faUsers, faUserTag, faEnvelopeOpenText, faChartLine, faHandshake,
  faBlog, faLayerGroup, faLightbulb, faQuestionCircle, faQuoteRight,
  faFolderOpen, faCode, faSearch, faBell, faFileContract, faUserShield,
  faCog, faGraduationCap, faBuilding, faAngleLeft, faAngleRight, faSignOutAlt,
  faChevronDown, faTimes, faTasks
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS, ROUTES } from '../constants';
import AdminMetaTagsUpdater from './AdminMetaTagsUpdater';

const ALL_NAV_GROUPS = [
  {
    name: "Dashboard",
    icon: faGauge,
    path: ROUTES.DASHBOARD,
    isGroup: false,
    permission: null,
  },
  {
    name: "User Management",
    icon: faUsers,
    isGroup: true,
    permission: PERMISSIONS.MANAGE_USERS,
    links: [
      { path: ROUTES.USERS, name: "Users", icon: faUsers, permission: PERMISSIONS.MANAGE_USERS },
      { path: ROUTES.ROLES, name: "Roles", icon: faUserTag, permission: PERMISSIONS.MANAGE_ROLES },
    ]
  },
  {
    name: "Lead Management",
    icon: faEnvelopeOpenText,
    isGroup: true,
    permission: PERMISSIONS.MANAGE_LEADS,
    links: [
      { path: ROUTES.CONTACT_LEADS, name: "Leads", icon: faEnvelopeOpenText, permission: PERMISSIONS.MANAGE_LEADS },
      { path: ROUTES.LEAD_STATUS, name: "Status", icon: faChartLine, permission: PERMISSIONS.MANAGE_LEAD_STATUSES },
      { path: ROUTES.NOTIFICATION_SETTINGS, name: "Notification Settings", icon: faBell, permission: PERMISSIONS.MANAGE_NOTIFICATION_SETTINGS },
    ]
  },
  {
    name: "Newsletter Subscribers",
    icon: faEnvelopeOpenText,
    path: ROUTES.NEWSLETTER_SUBSCRIBERS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_NEWSLETTER_SUBSCRIBERS,
  },
  {
    name: "Services",
    icon: faHandshake,
    path: ROUTES.SERVICES,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_SERVICES,
  },
  {
    name: "Blog Management",
    icon: faBlog,
    isGroup: true,
    permission: PERMISSIONS.MANAGE_BLOGS,
    links: [
      { path: ROUTES.BLOGS, name: "Blogs", icon: faBlog, permission: PERMISSIONS.MANAGE_BLOGS },
      { path: ROUTES.BLOG_CATEGORIES, name: "Categories", icon: faLayerGroup, permission: PERMISSIONS.MANAGE_CATEGORIES },
    ]
  },
  {
    name: "Jobs",
    icon: faTasks,
    path: ROUTES.JOBS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_JOBS,
  },
  {
    name: "Skills",
    icon: faLightbulb,
    path: ROUTES.SKILLS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_SKILLS,
  },
  {
    name: "FAQs",
    icon: faQuestionCircle,
    path: ROUTES.FAQS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_FAQS,
  },
  {
    name: "Testimonials",
    icon: faQuoteRight,
    path: ROUTES.TESTIMONIALS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_TESTIMONIALS,
  },
  {
    name: "Projects",
    icon: faFolderOpen,
    path: ROUTES.PROJECTS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_PROJECTS,
  },
  {
    name: "Header/Footer",
    icon: faCode,
    path: ROUTES.HEADER_FOOTER,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_HEADER_FOOTER,
  },
  {
    name: "SEO Settings",
    icon: faSearch,
    path: ROUTES.SEO_SETTINGS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_SEO_SETTINGS,
  },
  {
    name: "Terms & Conditions",
    icon: faFileContract,
    path: ROUTES.TERMS_CONDITIONS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_TERMS_CONDITIONS,
  },
  {
    name: "Privacy Policy",
    icon: faUserShield,
    path: ROUTES.PRIVACY_POLICY,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_PRIVACY_POLICY,
  },
  {
    name: "Work & Education",
    icon: faGraduationCap,
    path: ROUTES.WORK_EDUCATION,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_WORK_EDUCATION,
  },
  {
    name: "Clients",
    icon: faBuilding,
    path: ROUTES.CLIENTS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_CLIENTS,
  },
  {
    name: "Settings",
    icon: faCog,
    path: ROUTES.SETTINGS,
    isGroup: false,
    permission: PERMISSIONS.MANAGE_SETTINGS,
  },
];

const AdminLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // New state for mobile sidebar
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPageTitle, setCurrentPageTitle] = useState("Dashboard");
  const { logout, siteName, hasPermission, user } = useAuth();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    if (!isSidebarCollapsed) {
      setOpenGroup(null);
    }
  };

  const toggleMobileSidebar = () => { // New function to toggle mobile sidebar
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleGroup = (groupName) => {
    setOpenGroup(prevOpenGroup => (prevOpenGroup === groupName ? null : groupName));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navGroups = useMemo(() => {
    return ALL_NAV_GROUPS.filter(group => {
      if (group.permission === null) return true;
      if (group.isGroup) {
        const hasGroupPermission = hasPermission(group.permission);
        const hasAnyLinkPermission = group.links.some(link => hasPermission(link.permission));
        return hasGroupPermission || hasAnyLinkPermission;
      }
      return hasPermission(group.permission);
    }).map(group => {
      if (group.isGroup) {
        return {
          ...group,
          links: group.links.filter(link => hasPermission(link.permission)),
        };
      }
      return group;
    });
  }, [hasPermission]);

  useEffect(() => {
    const currentPath = location.pathname;
    let title = "Dashboard";

    const allPossibleNavLinks = ALL_NAV_GROUPS.flatMap(group => group.isGroup ? group.links : [group]);

    const getBaseName = (segment) => {
      const baseLink = allPossibleNavLinks.find(link => link.path && link.path.startsWith(`/${segment}`));
      return baseLink ? baseLink.name : (segment.charAt(0).toUpperCase() + segment.slice(1)).replace(/-/g, ' ');
    };

    const exactMatch = allPossibleNavLinks.find(link => link.path === currentPath);
    if (exactMatch) {
      title = exactMatch.name;
    } else {
      const pathSegments = currentPath.split('/').filter(Boolean);

      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        const secondLastSegment = pathSegments.length > 1 ? pathSegments[pathSegments.length - 2] : null;

        if (lastSegment === 'trash') {
          if (secondLastSegment) {
            title = `Trashed ${getBaseName(secondLastSegment)}`;
          } else {
            title = `Trashed ${getBaseName(pathSegments[0])}`;
          }
        } else if (lastSegment === 'create') {
          if (secondLastSegment) {
            title = `Add ${getBaseName(secondLastSegment).replace(/s$/, '')}`;
          } else {
            title = `Add ${getBaseName(pathSegments[0]).replace(/s$/, '')}`;
          }
        } else if (!isNaN(lastSegment) && pathSegments.length > 1) {
          const baseSegment = pathSegments[pathSegments.length - 2];
          title = `View ${getBaseName(baseSegment).replace(/s$/, '')}`;
        } else {
          const directSegmentMatch = allPossibleNavLinks.find(link => link.path === `/${lastSegment}`);
          if (directSegmentMatch) {
            title = directSegmentMatch.name;
          } else {
            title = getBaseName(lastSegment);
          }
        }
      }
    }
    setCurrentPageTitle(title);
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${currentPageTitle} | ${siteName}`;
  }, [currentPageTitle, siteName]);

  return (
    <div className="flex h-screen bg-bg-dark text-text-white font-sans">
      <AdminMetaTagsUpdater />
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleMobileSidebar}
        ></div>
      )}

      <aside className={`fixed inset-y-0 left-0 bg-gray-800 text-white flex flex-col transition-all duration-300 z-50
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:translate-x-0 ${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}`}>
        <div className="p-4 text-2xl font-bold border-b border-gray-700 flex items-center justify-between">
          <span className={`text-2xl font-bold text-text-white overflow-hidden ${isSidebarCollapsed ? 'w-0' : 'w-auto'}`}>
            Rishabh <span className="text-accent-red">Gupta</span>
          </span>
          <span className={`text-2xl font-bold text-text-white ${isSidebarCollapsed ? 'block' : 'hidden'}`}>R</span>
          <button onClick={toggleSidebar} className="text-white focus:outline-none hidden lg:block"> {/* Hide on mobile */}
            <FontAwesomeIcon icon={isSidebarCollapsed ? faAngleRight : faAngleLeft} />
          </button>
          <button onClick={toggleMobileSidebar} className="text-white focus:outline-none lg:hidden"> {/* Mobile close button */}
            <FontAwesomeIcon icon={faTimes} /> {/* Using faTimes for close button */}
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto thin-scrollbar">
          {navGroups.map((item, index) => (
            item.isGroup ? (
              <div key={index}>
                <button
                  onClick={() => toggleGroup(item.name)}
                  className={`flex items-center w-full py-2 px-4 rounded transition-colors duration-200 hover:bg-gray-700 ${openGroup === item.name ? 'bg-gray-700' : ''}`}
                >
                  <FontAwesomeIcon icon={item.icon} className={`${isSidebarCollapsed ? 'mr-0' : 'mr-3'} text-lg`} />
                  <span className={`overflow-hidden whitespace-nowrap ${isSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                    {item.name}
                  </span>
                  {!isSidebarCollapsed && (
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`ml-auto transition-transform duration-300 ${openGroup === item.name ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>
                {!isSidebarCollapsed && openGroup === item.name && (
                  <div className="ml-6 mt-1 space-y-1">
                    {item.links.map((link, subIndex) => (
                      <NavLink
                        key={subIndex}
                        to={link.path}
                        className={({ isActive }) =>
                          `flex items-center py-2 px-4 rounded transition-colors duration-200 ${
                            isActive ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'
                          }`
                        }
                        onClick={toggleMobileSidebar} // Close sidebar on link click
                      >
                        <FontAwesomeIcon icon={link.icon} className="mr-3 text-sm" />
                        <span className="overflow-hidden whitespace-nowrap">{link.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={index}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center py-2 px-4 rounded transition-colors duration-200 ${
                    isActive ? 'bg-gray-700 text-white' : 'hover:bg-gray-700'
                  }`
                }
                onClick={toggleMobileSidebar} // Close sidebar on link click
              >
                <FontAwesomeIcon icon={item.icon} className={`${isSidebarCollapsed ? 'mr-0' : 'mr-3'} text-lg`} />
                <span className={`overflow-hidden whitespace-nowrap ${isSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                  {item.name}
                </span>
              </NavLink>
            )
          ))}
        </nav>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full bg-accent-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className={`${isSidebarCollapsed ? 'mr-0' : 'mr-2'} text-lg`} />
            <span className={`overflow-hidden whitespace-nowrap ${isSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
              Sign Out
            </span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminNavbar currentPageTitle={currentPageTitle} firstName={user?.first_name} toggleSidebar={toggleMobileSidebar} /> {/* Pass toggleMobileSidebar */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-bg-dark text-text-white p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;